import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { AdminStatusPill } from './AdminStatusPill';
import {
  GLASS_CARD_TILE,
  glassWashClass,
  type GlassWashTone,
} from '@/shared/ui-class';
import { cn } from '@/shared/cn';

type AdminStatCardBaseProps = {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  alert?: ReactNode;
  tone?: GlassWashTone;
  valueIsElement?: boolean;
};

export type AdminStatCardProps = AdminStatCardBaseProps &
  (
    | {
        href: string;
        onClick?: never;
        active?: never;
      }
    | {
        href?: undefined;
        onClick: () => void;
        active?: boolean;
      }
  );

export function AdminStatCard({
  href,
  onClick,
  active = false,
  label,
  value,
  sub,
  alert,
  tone = 'default',
  valueIsElement = false,
}: AdminStatCardProps) {
  const isLink = href !== undefined;
  const className = cn(
    'group flex h-full min-w-0 flex-col p-5',
    GLASS_CARD_TILE,
    glassWashClass(tone),
    !isLink && 'cursor-pointer text-left',
    !isLink &&
      active &&
      'bg-[var(--glass-surface-strong)] ring-1 ring-inset ring-[color:rgb(166_17_58_/_0.35)] dark:ring-[color:rgb(201_38_78_/_0.45)]',
  );

  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-muted">
          {label}
        </p>
        {isLink ? (
          <ChevronRight
            aria-hidden
            className="h-4 w-4 shrink-0 text-zinc-400 opacity-60 transition group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-zinc-500"
          />
        ) : null}
      </div>
      <div className="mt-2 flex h-9 min-w-0 items-center overflow-hidden">
        {valueIsElement ? (
          <div className="min-w-0 overflow-hidden">{value}</div>
        ) : (
          <p className="min-w-0 truncate text-3xl font-semibold leading-9 tracking-tight text-zinc-900 dark:text-zinc-100">
            {value}
          </p>
        )}
      </div>
      <p className="mt-1 min-h-5 text-sm text-zinc-600 dark:text-zinc-400">
        {sub}
      </p>
      <div className="mt-auto flex min-h-6 items-end pt-2">{alert}</div>
    </>
  );

  if (isLink) {
    return (
      <Link to={href} className={className}>
        {body}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={className}
    >
      {body}
    </button>
  );
}

export function AdminStatUnavailable() {
  return (
    <span className="text-zinc-400 dark:text-zinc-500" aria-label="Unavailable">
      —
    </span>
  );
}

export function AdminSystemReadinessPill({
  status,
}: {
  status: 'ok' | 'warming' | 'draining';
}) {
  return <AdminStatusPill status={status} />;
}
